import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ParticipantService } from '../../../services/participant.service';
import { EnrollmentService } from '../../../services/enrollment.service';
import { CorporateEnrollmentService } from '../../../services/corporate-enrollment.service';
import { EnrollmentTransferService, CreateEnrollmentTransferDto, EnrollmentTransferValidation } from '../../../services/enrollment-transfer.service';
import { LoadingService } from '../../../services/loading.service';
import { ToastService } from '../../../services/toast.service';

type EnrollmentType = 'individual' | 'corporate';

@Component({
  selector: 'app-new-transfer',
  templateUrl: './new-transfer.component.html',
  styleUrls: ['./new-transfer.component.scss']
})
export class NewTransferComponent implements OnInit {
  step: number = 1;

  // Tipo de inscripción
  enrollmentType: EnrollmentType = 'individual';

  // Participantes
  participants: any[] = [];
  fromParticipantId: number | null = null;
  toParticipantId: number | null = null;

  // Inscripciones individuales del participante origen
  enrollments: any[] = [];
  selectedEnrollmentId: number | null = null;

  // Inscripciones empresariales del participante origen
  corporateEnrollments: any[] = [];
  selectedCorporateEnrollment: any = null;
  selectedCorporateEnrollmentParticipantId: number | null = null;

  // Motivo de la cesión
  reason: string = '';

  // Validación
  validation: EnrollmentTransferValidation | null = null;
  validationLoading: boolean = false;

  constructor(
    private participantService: ParticipantService,
    private enrollmentService: EnrollmentService,
    private corporateEnrollmentService: CorporateEnrollmentService,
    private transferService: EnrollmentTransferService,
    private loadingService: LoadingService,
    private toastService: ToastService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.loadParticipants();
  }

  loadParticipants(): void {
    this.participantService.getAll().subscribe({
      next: (res: any) => {
        if (res && res.data) {
          // Manejar estructura paginada: data puede ser array directo o {data: [...], totalCount: ...}
          this.participants = Array.isArray(res.data) ? res.data : (res.data?.data || []);
        }
      },
      error: () => {
        this.toastService.showError('No se pudieron cargar los participantes');
      }
    });
  }

  // 🔍 Búsqueda server-side para participantes
  onSearchParticipants(event: any): void {
    const query = event.filter;
    if (!query || query.length < 3) {
      this.participantService.getAll().subscribe({
        next: (res: any) => {
          if (res && res.data) {
            this.participants = Array.isArray(res.data) ? res.data : (res.data?.data || []);
          }
        },
        error: (err: any) => console.error('Error loading participants:', err)
      });
      return;
    }

    this.participantService.getAllFiltered(query).subscribe({
      next: (res: any) => {
        if (res && res.data) {
          this.participants = Array.isArray(res.data) ? res.data : (res.data?.data || []);
        }
      },
      error: (err: any) => console.error('Error searching participants:', err)
    });
  }

  onEnrollmentTypeChange(): void {
    // Limpiar selecciones al cambiar tipo
    this.enrollments = [];
    this.corporateEnrollments = [];
    this.selectedEnrollmentId = null;
    this.selectedCorporateEnrollment = null;
    this.selectedCorporateEnrollmentParticipantId = null;
    this.validation = null;

    // Recargar inscripciones si ya hay participante seleccionado
    if (this.fromParticipantId) {
      this.loadEnrollmentsForParticipant(this.fromParticipantId);
    }
  }

  onFromParticipantChange(): void {
    this.enrollments = [];
    this.corporateEnrollments = [];
    this.selectedEnrollmentId = null;
    this.selectedCorporateEnrollment = null;
    this.selectedCorporateEnrollmentParticipantId = null;
    this.validation = null;

    if (this.fromParticipantId) {
      this.loadEnrollmentsForParticipant(this.fromParticipantId);
    }
  }

  loadEnrollmentsForParticipant(participantId: number): void {
    if (this.enrollmentType === 'individual') {
      this.loadIndividualEnrollments(participantId);
    } else {
      this.loadCorporateEnrollments(participantId);
    }
  }

  loadIndividualEnrollments(participantId: number): void {
    this.loadingService.show();
    this.enrollmentService.getEnrollmentsByStudentId(participantId).subscribe({
      next: (res: any) => {
        if (res.isSuccess && res.data) {
          // Filtrar solo inscripciones activas (status = 1 o 'Active')
          this.enrollments = res.data.filter((e: any) =>
            e.status === 1 || e.status === 'Active' || String(e.status).toLowerCase() === 'active'
          );
        } else {
          this.enrollments = [];
        }
        this.loadingService.hide();
      },
      error: () => {
        this.enrollments = [];
        this.loadingService.hide();
        this.toastService.showError('No se pudieron cargar las inscripciones individuales');
      }
    });
  }

  loadCorporateEnrollments(participantId: number): void {
    this.loadingService.show();
    this.corporateEnrollmentService.getCorporateEnrollmentsByParticipant(participantId).subscribe({
      next: (res: any) => {
        if (res.isSuccess && res.data) {
          // Filtrar solo inscripciones corporativas activas
          this.corporateEnrollments = res.data.filter((ce: any) =>
            ce.status === 1 || ce.status === 'Active' || String(ce.status).toLowerCase() === 'active'
          );
        } else {
          this.corporateEnrollments = [];
        }
        this.loadingService.hide();
      },
      error: () => {
        this.corporateEnrollments = [];
        this.loadingService.hide();
        this.toastService.showError('No se pudieron cargar las inscripciones empresariales');
      }
    });
  }

  onToParticipantChange(): void {
    this.validation = null;
    // Revalidar si ya hay una inscripción seleccionada
    if (this.hasValidEnrollmentSelection() && this.toParticipantId) {
      this.validateTransfer();
    }
  }

  hasValidEnrollmentSelection(): boolean {
    if (this.enrollmentType === 'individual') {
      return this.selectedEnrollmentId !== null;
    } else {
      return this.selectedCorporateEnrollment !== null && this.selectedCorporateEnrollmentParticipantId !== null;
    }
  }

  selectEnrollment(enrollmentId: number): void {
    this.selectedEnrollmentId = enrollmentId;
    this.validation = null;
  }

  selectCorporateEnrollment(corpEnrollment: any): void {
    this.selectedCorporateEnrollment = corpEnrollment;
    // El backend ya incluye el CorporateEnrollmentParticipantId del participante específico
    this.selectedCorporateEnrollmentParticipantId = corpEnrollment.corporateEnrollmentParticipantId || null;
    this.validation = null;
  }

  validateTransfer(): void {
    if (!this.toParticipantId || !this.fromParticipantId) {
      return;
    }

    let dto: CreateEnrollmentTransferDto;

    if (this.enrollmentType === 'individual') {
      if (!this.selectedEnrollmentId) return;
      dto = {
        isCorporate: false,
        enrollmentId: this.selectedEnrollmentId,
        fromParticipantId: this.fromParticipantId,
        toParticipantId: this.toParticipantId,
        reason: this.reason || undefined
      };
    } else {
      if (!this.selectedCorporateEnrollment || !this.selectedCorporateEnrollmentParticipantId) return;
      dto = {
        isCorporate: true,
        corporateEnrollmentId: this.selectedCorporateEnrollment.id,
        corporateEnrollmentParticipantId: this.selectedCorporateEnrollmentParticipantId,
        fromParticipantId: this.fromParticipantId,
        toParticipantId: this.toParticipantId,
        reason: this.reason || undefined
      };
    }

    this.validationLoading = true;
    this.transferService.validateTransfer(dto).subscribe({
      next: (res: any) => {
        if (res.isSuccess && res.data) {
          this.validation = res.data;
        }
        this.validationLoading = false;
      },
      error: (err) => {
        this.validationLoading = false;
        this.toastService.showError(err.message || 'Error al validar la transferencia');
      }
    });
  }

  // Navegación entre pasos
  nextStep(): void {
    if (this.step === 1) {
      if (!this.fromParticipantId || !this.toParticipantId) {
        this.toastService.showWarning('Debe seleccionar ambos participantes');
        return;
      }
      if (this.fromParticipantId === this.toParticipantId) {
        this.toastService.showWarning('El participante origen y destino no pueden ser el mismo');
        return;
      }
      this.step = 2;
    } else if (this.step === 2) {
      if (!this.hasValidEnrollmentSelection()) {
        this.toastService.showWarning('Debe seleccionar una inscripción');
        return;
      }
      // Validar la transferencia antes de pasar al resumen
      this.validateTransfer();
      this.step = 3;
    }
  }

  previousStep(): void {
    if (this.step > 1) {
      this.step--;
    }
  }

  canProceedToSummary(): boolean {
    return !!(this.validation?.isValid);
  }

  submitTransfer(): void {
    if (!this.validation?.isValid) {
      this.toastService.showError('La transferencia no es válida. Por favor revise los errores.');
      return;
    }

    let payload: CreateEnrollmentTransferDto;

    if (this.enrollmentType === 'individual') {
      payload = {
        isCorporate: false,
        enrollmentId: this.selectedEnrollmentId!,
        fromParticipantId: this.fromParticipantId!,
        toParticipantId: this.toParticipantId!,
        reason: this.reason || undefined
      };
    } else {
      payload = {
        isCorporate: true,
        corporateEnrollmentId: this.selectedCorporateEnrollment.id,
        corporateEnrollmentParticipantId: this.selectedCorporateEnrollmentParticipantId!,
        fromParticipantId: this.fromParticipantId!,
        toParticipantId: this.toParticipantId!,
        reason: this.reason || undefined
      };
    }

    this.loadingService.show();
    this.transferService.createTransfer(payload).subscribe({
      next: (res: any) => {
        this.loadingService.hide();
        if (res.isSuccess) {
          this.toastService.showSuccess('Cesión de cupo realizada correctamente');
          this.router.navigate(['/enrollment-transfers']);
        } else {
          this.toastService.showError(res.errorMessage || 'Error al crear la cesión de cupo');
        }
      },
      error: (err) => {
        this.loadingService.hide();
        this.toastService.showError(err.message || 'Error al crear la cesión de cupo');
      }
    });
  }

  // Helpers para el template
  getParticipantName(participantId: number): string {
    const participant = this.participants.find(p => p.id === participantId);
    if (participant) {
      return `${participant.firstName} ${participant.lastName}`;
    }
    return 'N/A';
  }

  getParticipantDocument(participantId: number): string {
    const participant = this.participants.find(p => p.id === participantId);
    if (participant) {
      return `${participant.typeDocument} ${participant.documentId}`;
    }
    return 'N/A';
  }

  getSelectedEnrollment(): any {
    if (this.enrollmentType === 'individual') {
      return this.enrollments.find(e => e.id === this.selectedEnrollmentId);
    } else {
      return this.selectedCorporateEnrollment;
    }
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  }

  goBack(): void {
    this.router.navigate(['/enrollment-transfers']);
  }
}
